import { useEffect, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eraser, FileSignature, Save } from "lucide-react";
import { toast } from "sonner";
import { Button, ButtonLink, Card, FieldError, Input, Label, PageHeader, Select, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api, ApiError } from "../lib/api";
import { useRouter } from "../lib/router";
import type { RatDetail, RatStatus } from "../types/api";

type FormState = {
  chamadoId: string;
  dataAtendimento: string;
  nomeTecnico: string;
  empresaAtendida: string;
  codigoEmpresaAtendida: string;
  nomeContatoCliente: string;
  emailContatoCliente: string;
  descricaoAtendimento: string;
  materialUtilizado: string;
  observacoes: string;
  nomeAssinante: string;
};

function nowLocalIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function emptyForm(nomeTecnico: string): FormState {
  return {
    chamadoId: "",
    dataAtendimento: nowLocalIso(),
    nomeTecnico,
    empresaAtendida: "",
    codigoEmpresaAtendida: "",
    nomeContatoCliente: "",
    emailContatoCliente: "",
    descricaoAtendimento: "",
    materialUtilizado: "",
    observacoes: "",
    nomeAssinante: "",
  };
}

/**
 * Área de assinatura tocável para celular: usa Pointer Events (não só mouse)
 * para funcionar com o dedo em touchscreen, mouse ou caneta. O traço é
 * desenhado em um <canvas> e, ao salvar, é serializado como PNG base64.
 *
 * `initialValue` permite exibir uma assinatura já salva anteriormente (data
 * URI PNG) ao abrir um RAT existente — sem isso o canvas fica em branco
 * mesmo quando o valor está corretamente salvo no banco. `readOnly` desenha
 * a assinatura mas desativa os eventos de ponteiro (usado para RATs já
 * concluídos, onde reassinar não faz sentido).
 */
function SignaturePad({
  onChange,
  initialValue,
  readOnly,
}: {
  onChange: (dataUrl: string | null) => void;
  initialValue?: string | null;
  readOnly?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  function getContext() {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext("2d");
  }

  // Redimensiona o canvas de acordo com o tamanho real em tela (considerando
  // devicePixelRatio) para o traço não ficar borrado em telas de celular.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#111827";
    }
  }, []);

  // Desenha a assinatura previamente salva (carregada de um RAT existente)
  // assim que ela chega. Não depende do estado que o próprio usuário altera
  // ao desenhar um novo traço — o pai só passa `initialValue` uma vez, ao
  // carregar o registro, então isso não briga com o desenho ao vivo.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialValue) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      // rect.width/height porque o contexto já está escalado por
      // devicePixelRatio (ctx.scale acima) — desenhar usando canvas.width
      // (já multiplicado pelo ratio) duplicaria a escala.
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      hasStrokeRef.current = true;
    };
    img.src = initialValue;
  }, [initialValue]);

  function pointFromEvent(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (readOnly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(event);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (readOnly || !drawingRef.current) return;
    const ctx = getContext();
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    const point = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    hasStrokeRef.current = true;
  }

  function finishStroke() {
    if (readOnly) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    if (hasStrokeRef.current && canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  }

  function limpar() {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokeRef.current = false;
    onChange(null);
  }

  return (
    <div className="rat-signature-pad">
      <canvas
        ref={canvasRef}
        className="rat-signature-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
        onPointerCancel={finishStroke}
        style={{ touchAction: readOnly ? "auto" : "none" }}
      />
      {!readOnly && (
        <Button type="button" variant="secondary" onClick={limpar} className="rat-signature-clear">
          <Eraser size={16} /> Limpar assinatura
        </Button>
      )}
    </div>
  );
}

export function RatPage({ id }: { id?: string }) {
  const { session } = useAuth();
  const { navigate } = useRouter();
  const [savedId, setSavedId] = useState<number | null>(id ? Number(id) : null);
  const [form, setForm] = useState<FormState>(() => emptyForm(session?.login ?? ""));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [assinaturaBase64, setAssinaturaBase64] = useState<string | null>(null);
  // Assinatura tal como veio do banco ao carregar um RAT existente — usada só
  // para desenhar o estado inicial do canvas, nunca atualizada pelos traços
  // do próprio usuário (que ficam em `assinaturaBase64`).
  const [assinaturaSalva, setAssinaturaSalva] = useState<string | null>(null);
  const [status, setStatus] = useState<RatStatus>("rascunho");
  const [saving, setSaving] = useState<"rascunho" | "concluido" | null>(null);
  const [loaded, setLoaded] = useState(!id);

  const companies = useQuery({
    queryKey: ["companies", "rat"],
    queryFn: api.getCompanies,
  });

  // RAT já concluído: registro fechado, exibido apenas para consulta (inclui
  // a assinatura colhida). Reassinar/editar um RAT concluído não faz sentido.
  const somenteLeitura = Boolean(id) && status === "concluido";

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    (async () => {
      try {
        const result = await api.getRat(Number(id));
        const item = result?.[0] as RatDetail | undefined;
        if (!item || cancelado) return;
        setSavedId(item.Id);
        setStatus(item.status);
        setForm({
          chamadoId: item.ChamadoId ? String(item.ChamadoId) : "",
          dataAtendimento: item.DataAtendimento ? item.DataAtendimento.slice(0, 16) : nowLocalIso(),
          nomeTecnico: item.NomeTecnico ?? "",
          empresaAtendida: item.EmpresaAtendida ?? "",
          codigoEmpresaAtendida: item.CodigoEmpresaAtendida ? String(item.CodigoEmpresaAtendida) : "",
          nomeContatoCliente: item.NomeContatoCliente ?? "",
          emailContatoCliente: item.EmailContatoCliente ?? "",
          descricaoAtendimento: item.DescricaoAtendimento ?? "",
          materialUtilizado: item.MaterialUtilizado ?? "",
          observacoes: item.Observacoes ?? "",
          nomeAssinante: item.NomeAssinante ?? "",
        });
        setAssinaturaBase64(item.AssinaturaBase64 ?? null);
        setAssinaturaSalva(item.AssinaturaBase64 ?? null);
      } catch {
        toast.error("Não foi possível carregar o RAT salvo.");
      } finally {
        if (!cancelado) setLoaded(true);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [id]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function validar(status: "rascunho" | "concluido") {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.nomeTecnico.trim()) nextErrors.nomeTecnico = "Informe o técnico responsável.";
    if (!form.empresaAtendida.trim()) nextErrors.empresaAtendida = "Informe a empresa atendida.";
    if (!form.nomeContatoCliente.trim()) nextErrors.nomeContatoCliente = "Informe quem recebeu o atendimento.";
    if (form.emailContatoCliente.trim() && !/^\S+@\S+\.\S+$/.test(form.emailContatoCliente.trim()))
      nextErrors.emailContatoCliente = "Informe um e-mail válido.";
    if (!form.descricaoAtendimento.trim()) nextErrors.descricaoAtendimento = "Descreva o que foi feito no atendimento.";
    if (form.chamadoId.trim() && !/^\d+$/.test(form.chamadoId.trim()))
      nextErrors.chamadoId = "Informe apenas números.";

    if (status === "concluido" && !assinaturaBase64) {
      toast.error("Colete a assinatura do cliente antes de concluir o RAT.");
      return null;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0 ? true : null;
  }

  async function salvar(status: "rascunho" | "concluido") {
    if (!validar(status)) return;

    const payload = {
      chamadoId: form.chamadoId.trim() ? Number(form.chamadoId.trim()) : null,
      dataAtendimento: form.dataAtendimento,
      nomeTecnico: form.nomeTecnico.trim(),
      empresaAtendida: form.empresaAtendida.trim(),
      codigoEmpresaAtendida: form.codigoEmpresaAtendida ? Number(form.codigoEmpresaAtendida) : null,
      nomeContatoCliente: form.nomeContatoCliente.trim(),
      emailContatoCliente: form.emailContatoCliente.trim() || null,
      descricaoAtendimento: form.descricaoAtendimento.trim(),
      materialUtilizado: form.materialUtilizado.trim() || null,
      observacoes: form.observacoes.trim() || null,
      assinaturaBase64,
      nomeAssinante: form.nomeAssinante.trim() || null,
    };

    setSaving(status);
    try {
      const result = savedId
        ? await api.updateRat(savedId, payload, status)
        : await api.submitRat(payload, status);
      setSavedId(result.id);
      if (result.chamadoId) setField("chamadoId", String(result.chamadoId));

      if (status === "concluido") {
        toast.success(
          result.chamadoId
            ? `RAT concluído. Chamado #${result.chamadoId} encerrado automaticamente.`
            : "RAT concluído.",
        );
        navigate("/rat/lista");
      } else {
        toast.success("Rascunho salvo.");
      }
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : "Não foi possível salvar o RAT. Tente novamente.",
      );
    } finally {
      setSaving(null);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    salvar("concluido");
  }

  if (!loaded) return null;

  return (
    <div className="rat-page">
      <PageHeader
        eyebrow="RAT"
        title={savedId ? `RAT #${savedId}` : "Novo RAT"}
        description={
          somenteLeitura
            ? "RAT concluído — exibido apenas para consulta, incluindo a assinatura do cliente."
            : "Relatório de Atendimento Técnico — preencha ao final da visita e colha a assinatura do cliente."
        }
        actions={
          somenteLeitura ? (
            <ButtonLink to="/rat/lista" variant="secondary">
              Voltar para a lista
            </ButtonLink>
          ) : undefined
        }
      />

      <form onSubmit={submit} className="rat-form">
        <Card className="rat-section">
          <Label htmlFor="chamadoId">Número do chamado (opcional)</Label>
          <Input
            id="chamadoId"
            inputMode="numeric"
            placeholder="Deixe em branco para abrir um chamado novo"
            value={form.chamadoId}
            onChange={(event) => setField("chamadoId", event.target.value)}
            aria-invalid={Boolean(errors.chamadoId)}
            disabled={somenteLeitura}
          />
          <FieldError>{errors.chamadoId}</FieldError>
          {!somenteLeitura && (
            <p className="rat-hint">
              Se o cliente já tem um chamado aberto, informe o número aqui. Caso contrário, ao
              salvar este RAT um chamado novo é aberto automaticamente.
            </p>
          )}

          <Label htmlFor="dataAtendimento">Data e hora do atendimento</Label>
          <Input
            id="dataAtendimento"
            type="datetime-local"
            value={form.dataAtendimento}
            onChange={(event) => setField("dataAtendimento", event.target.value)}
            disabled={somenteLeitura}
          />

          <Label htmlFor="nomeTecnico">Técnico responsável</Label>
          <Input
            id="nomeTecnico"
            value={form.nomeTecnico}
            onChange={(event) => setField("nomeTecnico", event.target.value)}
            aria-invalid={Boolean(errors.nomeTecnico)}
            disabled={somenteLeitura}
          />
          <FieldError>{errors.nomeTecnico}</FieldError>
        </Card>

        <Card className="rat-section">
          <Label htmlFor="empresaAtendida">Empresa / cliente atendido</Label>
          <Select
            id="empresaAtendida"
            value={
              !form.codigoEmpresaAtendida && form.empresaAtendida ? "__legado__" : form.codigoEmpresaAtendida
            }
            disabled={companies.isLoading || somenteLeitura}
            onChange={(event) => {
              const codigo = event.target.value;
              if (codigo === "__legado__") return;
              const nome = companies.data?.find((company) => String(company.id) === codigo)?.empresa ?? "";
              setField("codigoEmpresaAtendida", codigo);
              setField("empresaAtendida", nome);
            }}
            aria-invalid={Boolean(errors.empresaAtendida)}
          >
            <option value="">
              {companies.isLoading ? "Carregando empresas..." : "Selecione a empresa"}
            </option>
            {/* RAT antigo (antes desta coluna existir) pode ter só o nome em texto,
                sem corresponder a nenhuma empresa cadastrada: mostra essa opção extra
                para não perder o valor já salvo até o técnico reselecionar. */}
            {!form.codigoEmpresaAtendida && form.empresaAtendida && (
              <option value="__legado__">{form.empresaAtendida} (selecione novamente)</option>
            )}
            {companies.data?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.empresa}
              </option>
            ))}
          </Select>
          <FieldError>{errors.empresaAtendida}</FieldError>

          <Label htmlFor="nomeContatoCliente">Nome de quem recebeu o atendimento</Label>
          <Input
            id="nomeContatoCliente"
            value={form.nomeContatoCliente}
            onChange={(event) => setField("nomeContatoCliente", event.target.value)}
            aria-invalid={Boolean(errors.nomeContatoCliente)}
            disabled={somenteLeitura}
          />
          <FieldError>{errors.nomeContatoCliente}</FieldError>

          <Label htmlFor="emailContatoCliente">E-mail de quem recebeu o atendimento (opcional)</Label>
          <Input
            id="emailContatoCliente"
            type="email"
            placeholder="cliente@empresa.com.br"
            value={form.emailContatoCliente}
            onChange={(event) => setField("emailContatoCliente", event.target.value)}
            aria-invalid={Boolean(errors.emailContatoCliente)}
            disabled={somenteLeitura}
          />
          <FieldError>{errors.emailContatoCliente}</FieldError>
          <p className="rat-hint">
            Se preenchido, ao concluir o RAT o sistema envia uma confirmação do atendimento pra esse e-mail.
          </p>

        </Card>

        <Card className="rat-section">
          <Label htmlFor="descricaoAtendimento">O que foi feito no atendimento</Label>
          <Textarea
            id="descricaoAtendimento"
            rows={6}
            value={form.descricaoAtendimento}
            onChange={(event) => setField("descricaoAtendimento", event.target.value)}
            aria-invalid={Boolean(errors.descricaoAtendimento)}
            disabled={somenteLeitura}
          />
          <FieldError>{errors.descricaoAtendimento}</FieldError>

          <Label htmlFor="materialUtilizado">Material utilizado no atendimento (opcional)</Label>
          <Textarea
            id="materialUtilizado"
            rows={2}
            placeholder="Ex: cabo de rede, conectores RJ45, ..."
            value={form.materialUtilizado}
            onChange={(event) => setField("materialUtilizado", event.target.value)}
            disabled={somenteLeitura}
          />

          <Label htmlFor="observacoes">Observações (opcional)</Label>
          <Textarea
            id="observacoes"
            rows={3}
            value={form.observacoes}
            onChange={(event) => setField("observacoes", event.target.value)}
            disabled={somenteLeitura}
          />
        </Card>

        <Card className="rat-section">
          <Label>
            <FileSignature size={16} /> Assinatura do cliente
          </Label>
          {!somenteLeitura && <p className="rat-hint">Peça para o cliente assinar com o dedo na tela abaixo.</p>}
          <SignaturePad
            onChange={setAssinaturaBase64}
            initialValue={assinaturaSalva}
            readOnly={somenteLeitura}
          />

          <Label htmlFor="nomeAssinante">Nome de quem assinou</Label>
          <Input
            id="nomeAssinante"
            value={form.nomeAssinante}
            onChange={(event) => setField("nomeAssinante", event.target.value)}
            disabled={somenteLeitura}
          />
        </Card>

        {!somenteLeitura && (
          <div className="rat-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={saving !== null}
              onClick={() => salvar("rascunho")}
              className="rat-actions-button"
            >
              <Save size={16} /> Salvar rascunho
            </Button>
            <Button type="submit" disabled={saving !== null} className="rat-actions-button">
              <FileSignature size={16} /> Concluir RAT
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
